import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

export interface ArtifactAssetDto {
  id: number;
  organizationId: number;
  uploadedByUserId?: number;
  ownerUserId?: number;
  artifactType: string;
  artifactCategory: string;
  artifactScope: string;
  isEnabled: boolean;
  default?: boolean | null;
  fileName: string;
  blobPath: string;
  contentType: string;
  sizeBytes: number;
  status: string;
  dateCreated: string;
}

export interface ArtifactAssetCreateDto {
  organizationId: number;
  eventId?: number;
  uploadedByUserId?: number;
  ownerUserId?: number;
  artifactType: string;
  artifactCategory?: string;
  artifactScope?: string;
  isEnabled?: boolean;
  default?: boolean | null;
  fileName: string;
  blobPath: string;
  contentType?: string;
  sizeBytes?: number;
}

export interface ArtifactAssetUploadDto {
  organizationId: number;
  eventId?: number;
  artifactType: string;
  artifactCategory?: string;
  artifactScope?: string;
  ownerUserId?: number;
  preferenceSection?: string;
  preferenceCategory?: string;
  preferenceKey?: string;
}

export interface ArtifactAssetSetEnabledDto {
  isEnabled: boolean;
}

export interface GamePreferenceResolutionDto {
  eventId: number;
  teamPlayerId: number;
  preferenceSection: string;
  preferenceCategory: string;
  source: string;
  artifactAssetId: number;
  fileName: string;
  blobPath: string;
  contentType: string;
}

export interface EventTimerSettingDto {
  id: number;
  organizationId: number;
  eventId: number;
  podiumTimerEnabled: boolean;
  podiumTimerSeconds: number;
  answerTimerEnabled: boolean;
  answerTimerSeconds: number;
  updatedByUserId?: number;
  dateCreated: string;
  dateModified?: string;
  isEnabled: boolean;
  isDeleted: boolean;
}

export interface EventTimerSettingUpsertDto {
  organizationId: number;
  eventId: number;
  podiumTimerEnabled: boolean;
  podiumTimerSeconds: number;
  answerTimerEnabled: boolean;
  answerTimerSeconds: number;
  updatedByUserId?: number;
}

@Injectable({ providedIn: 'root' })
export class GamePreferencesService {
  private readonly http = inject(HttpClient);
  private readonly artifactsApiUrl = '/api/artifactassets';
  private readonly effectiveMediaApiUrl = '/api/gamethemes/effective';
  private readonly timerSettingsApiUrl = '/api/eventtimersettings';

  getArtifactsByOrganization(organizationId: number): Observable<ArtifactAssetDto[]> {
    return this.http.get<ArtifactAssetDto[]>(`${this.artifactsApiUrl}/organization/${organizationId}`);
  }

  createArtifactMetadata(data: ArtifactAssetCreateDto): Observable<ArtifactAssetDto> {
    return this.http.post<ArtifactAssetDto>(`${this.artifactsApiUrl}/metadata`, data);
  }

  uploadArtifact(data: ArtifactAssetUploadDto, file: File): Observable<ArtifactAssetDto> {
    const form = new FormData();
    form.append('organizationId', String(data.organizationId));
    if (data.eventId) form.append('eventId', String(data.eventId));
    form.append('artifactType', data.artifactType);
    if (data.artifactCategory) form.append('artifactCategory', data.artifactCategory);
    if (data.artifactScope) form.append('artifactScope', data.artifactScope);
    if (data.ownerUserId) form.append('ownerUserId', String(data.ownerUserId));
    if (data.preferenceSection) form.append('preferenceSection', data.preferenceSection);
    if (data.preferenceCategory) form.append('preferenceCategory', data.preferenceCategory);
    if (data.preferenceKey) form.append('preferenceKey', data.preferenceKey);
    form.append('file', file);

    return this.http.post<ArtifactAssetDto>(`${this.artifactsApiUrl}/upload`, form);
  }

  replaceArtifact(id: number, file: File): Observable<ArtifactAssetDto> {
    const form = new FormData();
    form.append('file', file);
    return this.http.put<ArtifactAssetDto>(`${this.artifactsApiUrl}/${id}/replace`, form);
  }

  setArtifactDefault(id: number): Observable<ArtifactAssetDto> {
    return this.http.put<ArtifactAssetDto>(`${this.artifactsApiUrl}/${id}/default`, {});
  }

  deleteArtifact(id: number): Observable<void> {
    return this.http.delete<void>(`${this.artifactsApiUrl}/${id}`);
  }

  setArtifactEnabled(id: number, isEnabled: boolean): Observable<ArtifactAssetDto> {
    return this.http.put<ArtifactAssetDto>(`${this.artifactsApiUrl}/${id}/enabled`, { isEnabled } as ArtifactAssetSetEnabledDto);
  }

  getArtifactContent(id: number): Observable<Blob> {
    return this.http.get(`${this.artifactsApiUrl}/${id}/content`, { responseType: 'blob' });
  }

  resolveEffective(
    eventId: number,
    teamPlayerId: number,
    section: string,
    category: string
  ): Observable<GamePreferenceResolutionDto> {
    return this.http.get<GamePreferenceResolutionDto>(
      `${this.effectiveMediaApiUrl}?eventId=${eventId}&teamPlayerId=${teamPlayerId}&section=${encodeURIComponent(section)}&category=${encodeURIComponent(category)}`
    );
  }

  getTimerSettingsByEvent(eventId: number): Observable<EventTimerSettingDto | null> {
    return this.http.get<EventTimerSettingDto | null>(`${this.timerSettingsApiUrl}/event/${eventId}`);
  }

  upsertTimerSettings(data: EventTimerSettingUpsertDto): Observable<EventTimerSettingDto> {
    return this.http.post<EventTimerSettingDto>(this.timerSettingsApiUrl, data);
  }
}